import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Activity, AlertTriangle, Beaker, Gauge, RefreshCcw, Save, Sliders } from "lucide-react";
import { useAuth } from "../lib/auth";

const SRC_FIELDS = [
  { k: "src_credibility", l: "CREDIBILITY", desc: "Tier of the publishing domain (gov/edu/major newsroom)." },
  { k: "src_relevance",   l: "RELEVANCE",   desc: "How tightly the source matches the idea's topic." },
  { k: "src_recency",     l: "RECENCY",     desc: "How recent the idea is (decays over 30 days)." },
  { k: "src_consistency", l: "CONSISTENCY", desc: "Two independent high-credibility domains agreeing." },
  { k: "src_reuse_quality", l: "REUSE",     desc: "Cross-idea reuse of the source + verification bonus." },
];
const IDEA_FIELDS = [
  { k: "idea_source_rank", l: "SOURCE_RANK", desc: "Inherited composite source quality." },
  { k: "idea_novelty",     l: "NOVELTY",     desc: "How non-obvious / unprecedented the idea is." },
  { k: "idea_usefulness",  l: "USEFULNESS",  desc: "How actionable / applicable the idea is." },
  { k: "idea_cross_topic", l: "CROSS_TOPIC", desc: "Vocabulary overlap with active ideas in other libraries." },
  { k: "idea_evidence",    l: "EVIDENCE",    desc: "Credibility avg × source verification count." },
];

function Slider({ value, onChange, color = "bg-amber-400", testId }) {
  return (
    <input
      data-testid={testId}
      type="range"
      min={0}
      max={1}
      step={0.01}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-amber-400 cursor-pointer"
    />
  );
}

export default function RankingPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const { data: weights } = useQuery({
    queryKey: ["ranking-weights"],
    queryFn: () => api.get("/ranking/weights").then((r) => r.data),
  });
  const { data: audits = [] } = useQuery({
    queryKey: ["ranking-audits"],
    queryFn: () => api.get("/ranking/audits?limit=80").then((r) => r.data),
    refetchInterval: 10000,
  });

  const [draft, setDraft] = useState(null);
  const current = draft || weights || {};

  const change = (k, v) => setDraft({ ...(draft || weights), [k]: v });

  // Normalize sums to 1 (informational only — backend doesn't enforce)
  const sumSrc = useMemo(
    () => SRC_FIELDS.reduce((a, f) => a + Number(current[f.k] || 0), 0),
    [current],
  );
  const sumIdea = useMemo(
    () => IDEA_FIELDS.reduce((a, f) => a + Number(current[f.k] || 0), 0),
    [current],
  );

  const save = useMutation({
    mutationFn: () => api.put("/ranking/weights", draft || {}).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ranking-weights"] });
      setDraft(null);
      toast.success("WEIGHTS_SAVED · run RE-RANK to apply");
    },
    onError: (e) =>
      toast.error("SAVE_FAILED", { description: e?.response?.data?.detail || e?.message }),
  });
  const rerankIdeas = useMutation({
    mutationFn: () => api.post("/ranking/rerank/ideas").then((r) => r.data),
    onSuccess: (d) => toast.success(`RE-RANKED · ${d.updated} ideas · v${d.weights_version}`),
  });
  const rerankInnov = useMutation({
    mutationFn: () => api.post("/ranking/rerank/innovation").then((r) => r.data),
    onSuccess: (d) => toast.success(`RE-RANKED · ${d.updated} innovation items`),
  });

  const normalize = (which) => {
    const fields = which === "src" ? SRC_FIELDS : IDEA_FIELDS;
    const sum = fields.reduce((a, f) => a + Number(current[f.k] || 0), 0);
    if (sum <= 0) return;
    const next = { ...(draft || weights) };
    fields.forEach((f) => {
      next[f.k] = Math.round((Number(current[f.k]) / sum) * 100) / 100;
    });
    setDraft(next);
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-zinc-400 font-mono text-[12px]">
        <AlertTriangle className="inline mr-2 text-amber-400" size={14} />
        Admin or owner role required to view this page.
      </div>
    );
  }

  return (
    <div data-testid="ranking-page" className="p-4 space-y-4">
      <header className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-mono inline-flex items-center gap-2">
            <Sliders size={12} /> RANKING_&_INTELLIGENCE
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">Tune the scoring engine</h1>
          <p className="text-[12px] text-zinc-500 max-w-2xl mt-1">
            Adjust per-component weights, then RE-RANK to apply across all active ideas
            & innovation items. Every score change is recorded in the audit log below for full reproducibility.
          </p>
        </div>
        <div className="ml-auto flex items-end gap-3">
          <Stat label="WEIGHTS_VERSION" value={`v${weights?.version ?? 1}`} sub={weights?.updated_at?.slice(0, 16) || "—"} />
          <Stat label="AUDITS_LOGGED" value={audits.length} sub="last 80" color="text-purple-300" />
          <button
            data-testid="rerank-ideas"
            onClick={() => rerankIdeas.mutate()}
            disabled={rerankIdeas.isPending}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-emerald-400 text-emerald-200 hover:bg-emerald-400 hover:text-black disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCcw size={11} /> {rerankIdeas.isPending ? "WORKING…" : "RERANK_IDEAS"}
          </button>
          <button
            data-testid="rerank-innov"
            onClick={() => rerankInnov.mutate()}
            disabled={rerankInnov.isPending}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-purple-400 text-purple-200 hover:bg-purple-400 hover:text-black disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCcw size={11} /> {rerankInnov.isPending ? "WORKING…" : "RERANK_INNOV"}
          </button>
        </div>
      </header>

      {/* sliders */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300 inline-flex items-center gap-2">
              <Gauge size={11} /> SOURCE_WEIGHTS · sum {sumSrc.toFixed(2)}
            </span>
            <button
              data-testid="normalize-src"
              onClick={() => normalize("src")}
              className="text-[10px] font-mono uppercase text-zinc-500 hover:text-white"
            >
              NORMALIZE_→_1.0
            </button>
          </div>
          {SRC_FIELDS.map((f) => (
            <div key={f.k} className="mb-3">
              <div className="flex items-center text-[11px] font-mono">
                <span className="text-zinc-300 w-40 uppercase tracking-wider">{f.l}</span>
                <span className="ml-auto tabnum text-amber-300">{Number(current[f.k] || 0).toFixed(2)}</span>
              </div>
              <Slider
                value={Number(current[f.k] || 0)}
                onChange={(v) => change(f.k, v)}
                testId={`slider-${f.k}`}
              />
              <div className="text-[10px] text-zinc-500 mt-1">{f.desc}</div>
            </div>
          ))}
        </div>

        <div className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-purple-300 inline-flex items-center gap-2">
              <Beaker size={11} /> IDEA_WEIGHTS · sum {sumIdea.toFixed(2)}
            </span>
            <button
              data-testid="normalize-idea"
              onClick={() => normalize("idea")}
              className="text-[10px] font-mono uppercase text-zinc-500 hover:text-white"
            >
              NORMALIZE_→_1.0
            </button>
          </div>
          {IDEA_FIELDS.map((f) => (
            <div key={f.k} className="mb-3">
              <div className="flex items-center text-[11px] font-mono">
                <span className="text-zinc-300 w-40 uppercase tracking-wider">{f.l}</span>
                <span className="ml-auto tabnum text-purple-300">{Number(current[f.k] || 0).toFixed(2)}</span>
              </div>
              <Slider
                value={Number(current[f.k] || 0)}
                onChange={(v) => change(f.k, v)}
                testId={`slider-${f.k}`}
              />
              <div className="text-[10px] text-zinc-500 mt-1">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center gap-2">
        <button
          data-testid="save-weights"
          onClick={() => save.mutate()}
          disabled={!draft || save.isPending}
          className="h-9 px-4 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-amber-300 hover:border-amber-300 disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Save size={11} /> {save.isPending ? "SAVING…" : "SAVE_WEIGHTS"}
        </button>
        {draft && (
          <button
            onClick={() => setDraft(null)}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-400 hover:text-white"
            data-testid="discard-weights"
          >
            DISCARD_CHANGES
          </button>
        )}
        <span className="ml-auto text-[10px] font-mono text-zinc-500">
          Last updated by <span className="text-zinc-300">{weights?.updated_by || "system"}</span>
        </span>
      </div>

      {/* Audit log */}
      <section className="border border-[#27272A] overflow-x-auto">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
          <Activity size={11} /> SCORE_AUDITS · {audits.length} entries
        </div>
        <table className="w-full text-[11px] font-mono min-w-[1100px]">
          <thead className="text-zinc-500 bg-[#0E0E0E]">
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-2 uppercase">WHEN</th>
              <th className="text-left p-2 uppercase">KIND</th>
              <th className="text-left p-2 uppercase">ITEM_ID</th>
              <th className="text-right p-2 uppercase">SCORE</th>
              <th className="text-right p-2 uppercase">VERSION</th>
              <th className="text-left p-2 uppercase">REASON</th>
            </tr>
          </thead>
          <tbody>
            {audits.map((a) => (
              <tr key={a.id} data-testid={`audit-row-${a.id}`} className="border-b border-[#161616] hover:bg-[#0E0E0E]">
                <td className="p-2 text-zinc-400">{a.computed_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="p-2 uppercase text-purple-300">{a.kind}</td>
                <td className="p-2 text-zinc-500 truncate max-w-[180px]">{a.item_id}</td>
                <td className="p-2 text-right tabnum text-emerald-300">{Number(a.final_score).toFixed(2)}</td>
                <td className="p-2 text-right tabnum">v{a.weights_version}</td>
                <td className="p-2 text-zinc-300 max-w-[480px] truncate" title={a.reason}>{a.reason}</td>
              </tr>
            ))}
            {audits.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-zinc-500">NO_AUDITS · click RERANK_IDEAS</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] p-2 min-w-[120px]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</div>
      <div className={"text-xl font-mono tabnum mt-1 " + (color || "text-white")}>{value}</div>
      <div className="text-[10px] text-zinc-500 font-mono mt-1">{sub}</div>
    </div>
  );
}
